/**
 * Shared shapes and small display helpers for the request inspector.
 *
 * The badge variants map onto Bootstrap's `bg-*` utilities, which theme.css
 * re-colours from design tokens, so no component needs an inline colour
 * ternary.
 */

export interface Request {
  id: number;
  method: string;
  receivedAt: string;
  headers: Record<string, string>;
  queryParams: Record<string, string>;
  body: any;
  /**
   * The body exactly as it was sent, decoded as text.
   *
   * `body` is a parsed view of these bytes and is null when the payload never
   * parsed (a broken JSON body, which is precisely what the inspector is opened
   * for). Raw is what the details pane shows and what the cURL replay sends.
   * Absent/null on rows captured before the backend stored it.
   */
  bodyRaw?: string | null;
  ip?: string;
  userAgent?: string;
  /**
   * Sub-path the request was sent to, below the endpoint URL:
   * `/orders/created`. Absent/null for the bare endpoint and for rows captured
   * before the backend recorded it.
   */
  path?: string | null;
  /**
   * Status code the endpoint answered this request with. Absent/null on rows
   * captured before the backend recorded the answer — such a row has no
   * response record at all, and nothing about it should be rendered.
   */
  status?: number | null;
  /** Content type the answer carried, e.g. `application/json`. */
  responseContentType?: string | null;
  /**
   * Headers the answer carried, name to value. Absent/null on rows captured
   * before the backend recorded them — such a row must render without a header
   * list, not with an empty or `undefined` one.
   */
  responseHeaders?: Record<string, string> | null;
  /** Size of the answered body in bytes. */
  responseBodyBytes?: number | null;
  daysUntilDeletion: number;
}

/**
 * One row of the request list.
 *
 * The list is paged and rows arrive as previews: enough to render a row and
 * recognise a payload, deliberately without the headers/body blobs that made a
 * busy endpoint's first response hundreds of kilobytes. The full record comes
 * from `GET /request/:id` when the row is opened.
 */
export interface RequestPreview {
  id: number;
  method: string;
  receivedAt: string;
  path?: string | null;
  ip?: string;
  userAgent?: string;
  /** How many query parameters the request carried — the row shows the count, not the values. */
  queryParamCount: number;
  /** Size of the captured body in bytes; 0 when the request carried none. */
  bodyBytes: number;
  /** The head of the body, for the row and for filtering; null when there was no body. */
  bodyPreview?: string | null;
  /**
   * Status the endpoint answered with, for the row's badge; null on a row
   * captured before the answer was recorded. The rest of the response record
   * (content type, body size) is only in the full record.
   */
  status?: number | null;
  daysUntilDeletion: number;
}

export interface EndpointData {
  endpoint: {
    slug: string;
    created: string;
  };
  /** The loaded page(s) of rows, newest first — not necessarily every stored row. */
  requests: RequestPreview[];
  /**
   * Rows matching the active filter, however many are loaded here. Without a
   * filter this is every row the endpoint holds.
   */
  requestCount: number;
  /**
   * Every row the endpoint holds, whatever the filter is. The two differ only
   * while a filter is active, and the destructive actions read this one so they
   * cannot understate what they delete.
   */
  totalRequestCount?: number;
  /**
   * The distinct methods the endpoint holds, unfiltered — every verb the picker
   * may offer, not just the ones on the loaded rows.
   */
  methods?: string[];
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString();
}

/** Body size as a short human-readable string: `0 B`, `812 B`, `1.2 KB`. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${kilobytes < 10 ? kilobytes.toFixed(1) : Math.round(kilobytes)} KB`;
  }
  const megabytes = kilobytes / 1024;
  return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
}

/** `bg-*` variant for a captured request's HTTP method. */
export function methodVariant(method: string): string {
  switch ((method || '').toUpperCase()) {
    case 'GET':
      return 'info';
    case 'POST':
      return 'success';
    case 'PUT':
      return 'warning';
    case 'DELETE':
      return 'danger';
    default:
      return 'secondary';
  }
}

/** `bg-*` variant for the badge showing the status the endpoint answered with. */
export function statusVariant(status: number): string {
  if (status >= 200 && status < 300) {
    return 'success';
  }
  if (status >= 300 && status < 400) {
    return 'info';
  }
  if (status >= 400 && status < 500) {
    return 'warning';
  }
  if (status >= 500 && status < 600) {
    return 'danger';
  }
  // 1xx and anything outside the classes above: neither success nor failure.
  return 'secondary';
}

/** `bg-*` variant for the retention countdown badge. */
export function retentionVariant(daysUntilDeletion: number): string {
  if (daysUntilDeletion <= 1) {
    return 'danger';
  }
  if (daysUntilDeletion <= 2) {
    return 'warning';
  }
  return 'secondary';
}

export function retentionLabel(daysUntilDeletion: number): string {
  if (daysUntilDeletion <= 0) {
    return 'Deleting soon';
  }
  return `${daysUntilDeletion} day${daysUntilDeletion === 1 ? '' : 's'} left`;
}
